import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { caretRender, caretSelectionRender } from '@/lib/sync/collaboration/caret-render';
import Highlight from '@tiptap/extension-highlight';
import { Markdown } from '@tiptap/markdown';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import CharacterCount from '@tiptap/extension-character-count';
import Youtube from '@tiptap/extension-youtube';
import Color from '@tiptap/extension-color';
// @tiptap/extension-text-style has no default export in v3 — TextStyle is a
// named export (importing it as default throws at module load → white page).
import { TextStyle } from '@tiptap/extension-text-style';
import { Flex, ScrollRail, Text, Button } from '@nula/core-ui';
import { useEntityYjs } from '@/lib/sync/use-entity-yjs';
import type { YjsRoomKind } from '@/lib/sync/yjs-registry';
import { useAuthStore } from '@/stores/use-auth-store';
import { collabColorFromId } from '@/utils/collab-color';
import { createFilteredAwareness } from '@/lib/sync/collaboration/filter-cursor-states';
import { useCursorVisibility } from '@/lib/sync/collaboration/presence-indicator';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useHeadingAnchors } from '@/features/editor/hooks/use-heading-anchors';
import { usePreviewMarkup } from '@/features/editor/hooks/use-preview-markup';
import { Actionbar } from '@/components/actionbar/actionbar';
import { useSurfaceOrb } from '@/lib/documents/surface-orb-context';
import { usePublishAwarenessView } from '@/lib/sync/collaboration/use-publish-awareness-view';
import { BODY_VIEW_ID } from '@/lib/sync/collaboration/surface-view-id';
import { EditorSkeleton } from './editor-skeleton';
import { EntityTitleHeader } from '@/components/entity-title-header/entity-title-header';
import { EditorBubbleMenu } from './editor-bubble-menu';
import { requestGenerateFromSelection } from './generate-from-selection';
import { selectionTemplates } from '@/lib/templates/catalog';
import { useTemplates } from '@/lib/templates/use-templates-store';
import { EditorChartGutter } from './editor-chart-gutter';
import { buildChartEditRequest } from './chart-edit-request';
import { rangeToHtml } from './block-actions';
import { EditorTableMenu } from './editor-table-menu';
import { EditorBlockHandle } from './editor-block-handle';
import { EditorLinkEditor } from './editor-link-editor';
import { SlashSuggestionExtension } from './slash-menu/slash-suggestion';
import { CommandReferenceModal } from './slash-menu/slash-menu';
import { MarkdownPaste } from './markdown-paste';
import { YoutubePaste } from './youtube-paste';
import { EditorShortcuts } from './editor-shortcuts';
import { NulaLinkExtension } from './nula-link-extension';
import { LinkClickExtension } from './link-click-extension';
import { DocumentLink } from './document-link-extension';
import { EditorEntityLinkPreview } from './editor-entity-link-preview';
import { CalloutExtension } from './callout-extension';
import { CollapsibleExtensions } from './collapsible-extension';
import { CommentMark } from './comment-extension';
import { EditorComments } from './editor-comments';
import { EmojiExtension } from './emoji-extension';
import { EditorDocPicker } from './editor-doc-picker';
import { MarkdownCodeBlock, markdownTableKit } from './markdown-blocks';
import { lowlight } from './lowlight';
import { editorContentExtensions } from './editor-content-extensions';
import { MermaidExtension } from './mermaid-extension';
import { EditorEmojiPicker } from './editor-emoji-picker';
import { EditorFind } from './editor-find';
import { insertImageFiles, isImageFile } from './image-insert';
import { AutoJoinerExtension } from './auto-joiner-extension';
import { TrailingParagraph } from './trailing-paragraph-extension';
import { ReadOnlyGuard } from './read-only-guard-extension';
import { LinkUnfurlExtension } from './link-unfurl-extension';
import { CanvasExtension } from './canvas-extension';
import { EditorCanvasPicker } from './editor-canvas-picker';
import { LinkUnfurlPaste } from './link-unfurl-paste';
import { LinkUnfurlInput } from './link-unfurl-input';
import { useRegisterEditApplier } from '../hooks/use-register-edit-applier';
import { useEditAction } from '../hooks/use-edit-action';
import { SuggestionReviewTooltip } from '../extensions/ai-editing/suggestion-review-tooltip';
import { useReviewKeyboardShortcuts } from './rich-text-editor-keyboard';
import type { TiptapPending } from './tiptap-edit-applier';
import { AiEditing, getAiEditing } from '../extensions/ai-editing/ai-editing';
import type { EditEntityKind } from '@/lib/ai-composer/types';
import styles from './rich-text-editor.module.css';

export interface RichTextEditorProps {
  entityId: string;
  readOnly?: boolean;
  entityKindHint?: YjsRoomKind;
  showTitle?: boolean;
  showActionbar?: boolean;
  /** The Yjs fragment (Collaboration `field`) this editor binds to. Default 'prosemirror'. */
  fragmentName?: string;
  /**
   * Whether the viewer may mutate surfaces (create/rename/move/delete).
   * Defaults to true. Deliberately independent of `readOnly`: `readOnly` also
   * covers transient/display-only locks (a recording's apply window, an
   * oversized doc) that must NOT demote an editor's surface permission —
   * derive this from the sharing permission instead (ui-design.md §9.6).
   */
  canEditSurfaces?: boolean;
  offerGenerateFromSelection?: boolean;
  suspended?: boolean;
}

export function RichTextEditor({
  entityId,
  readOnly = false,
  entityKindHint,
  showTitle = true,
  showActionbar = true,
  fragmentName,
  canEditSurfaces = true,
  offerGenerateFromSelection = true,
  suspended = false,
}: RichTextEditorProps) {
  const interactive = !readOnly && !suspended;
  const lastSuspended = useRef(suspended);
  const { doc, awareness, synced } = useEntityYjs(entityId, entityKindHint);
  const SurfaceOrb = useSurfaceOrb();
  // Reported by the orb via onActiveViewChange (lib/documents/surface-orb-context.tsx):
  // features/document-surfaces owns the active surface, features/editor owns
  // Actionbar, and the two are separate ESLint zones. Stays undefined — and so
  // unfiltered — when no orb is mounted (read-only/embedded editors, canvases).
  const [presenceViewId, setPresenceViewId] = useState<string>();
  // Publish presence for every peer that has this editor open at all — NOT
  // gated on readOnly/synced/showActionbar&&!readOnly like the orb/Actionbar
  // render below. Presence filtering (filter-presence-by-view.ts) is
  // fail-closed: any peer who fails to publish a viewId is dropped from every
  // filtering peer's avatar list, silently and permanently. Only an embedded
  // editor (showActionbar=false — a document surface body, the recording HUD) is
  // exempt: its host already owns a presence mount of its own, and this would
  // double-publish onto the same awareness entry. Defaults to BODY_VIEW_ID
  // until the orb reports an active surface (or when there is no orb at all),
  // matching what a document's own SurfaceMount publishes for the body.
  usePublishAwarenessView(entityId, presenceViewId ?? BODY_VIEW_ID, showActionbar);
  // Wrap awareness so CollaborationCaret only renders un-hidden, non-idle remote
  // cursors (the presence-indicator eye toggles drive useCursorVisibility). The
  // real awareness is untouched — only what the caret renderer sees changes.
  const caretAwareness = useMemo(
    () => (awareness ? createFilteredAwareness(awareness) : null),
    [awareness],
  );
  // Re-render carets when the visibility toggles change: the filtered getStates
  // reads the store, but CollaborationCaret only recomputes on an awareness
  // 'update'. Emitting one (with no actual client changes) forces a recompute.
  const cursorAllHidden = useCursorVisibility((s) => s.allHidden);
  const cursorHiddenIds = useCursorVisibility((s) => s.hiddenIds);
  useEffect(() => {
    if (!awareness) return;
    awareness.emit('update', [{ added: [], updated: [], removed: [] }, 'cursor-visibility']);
  }, [awareness, cursorAllHidden, cursorHiddenIds]);
  const user = useAuthStore((s) => s.user);
  // Routes document-link clicks (nula://document/<id>) to in-app navigation —
  // see LinkClickExtension. useNavigateToEntity returns a NEW function each
  // render; passing it directly into the useEditor deps would recreate the
  // editor every render → setEditor → re-render → "Maximum update depth"
  // infinite loop. Hold it in a ref and expose a STABLE wrapper so the editor
  // is built once and always calls the latest navigation fn.
  const navigateToEntity = useNavigateToEntity();
  const navigateRef = useRef(navigateToEntity);
  navigateRef.current = navigateToEntity;
  const stableNavigate = useRef((id: string) => navigateRef.current(id)).current;
  // Same ref-indirection for in-document anchor jumps: jumpTo is created below
  // (it depends on `editor`, which is built FROM these extensions), so the
  // extension config can't reference it directly. stableJumpTo is referentially
  // stable and always calls the latest jumpTo via the ref.
  const jumpToRef = useRef<(id: string) => void>(() => {});
  const stableJumpTo = useRef((id: string) => jumpToRef.current(id)).current;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // Hidden file <input> for the "Image" slash command; and a stable ref to the
  // editor so editorProps.handlePaste/handleDrop (defined at useEditor-init,
  // before `editor` exists) can reach the live instance.
  const imageInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);

  // Map Yjs room kind → ai-composer EditEntityKind. Only document is wired
  // to replace-text in M2; other kinds are not registered.
  const editEntityKind: EditEntityKind | null =
    entityKindHint === 'document' ? entityKindHint : null;

  const canGenerateFromSelection =
    !readOnly &&
    editEntityKind === 'document' &&
    canEditSurfaces &&
    SurfaceOrb !== null &&
    offerGenerateFromSelection;
  const templates = useTemplates({ enabled: canGenerateFromSelection });
  const generateFromSelectionTemplates = useMemo(() => selectionTemplates(templates ?? []), [templates]);

  // Stable identity across re-renders: a fresh array each render makes Tiptap's
  // compareOptions call setOptions and reconfigure every ProseMirror plugin in place.
  // Keyed on the same inputs that gate editor recreation.
  const extensions = useMemo(
    () =>
      doc && awareness
        ? [
            ...editorContentExtensions({ onNavigate: stableNavigate, onAnchor: stableJumpTo }),
            Collaboration.configure({ document: doc, field: fragmentName ?? 'prosemirror' }),
            CollaborationCaret.configure({
              provider: { awareness: caretAwareness ?? awareness } as never,
              user: {
                name: user?.name ?? 'Anonymous',
                color: collabColorFromId(user?.sub ?? 'anonymous'),
              },
              render: caretRender,
              selectionRender: caretSelectionRender,
            }),
          ]
        : [StarterKit.configure({ codeBlock: false, link: false, trailingNode: false }), TrailingParagraph, DocumentLink.configure({ protocols: ['nula'] }), MarkdownCodeBlock.configure({ lowlight }), MermaidExtension, LinkClickExtension.configure({ onNavigate: stableNavigate, onAnchor: stableJumpTo }), Highlight.configure({ multicolor: true }), ...markdownTableKit({ resizable: true }), TextStyle, Color, TextAlign.configure({ types: ['heading', 'paragraph'] }), Image, Subscript, Superscript, CharacterCount, Youtube, Placeholder.configure({ placeholder: "Type '/' for commands" }), Markdown, MarkdownPaste, YoutubePaste, NulaLinkExtension, EmojiExtension, CalloutExtension, CommentMark, ...CollapsibleExtensions, EditorShortcuts, AutoJoinerExtension.configure({ nodeTypes: ['bulletList', 'orderedList'] }), LinkUnfurlExtension, LinkUnfurlPaste, LinkUnfurlInput, CanvasExtension, SlashSuggestionExtension, AiEditing, ReadOnlyGuard],
    // Key on user.sub/name, not the user object — the store can return a fresh object
    // reference with unchanged fields, which would needlessly rebuild the array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc, awareness, caretAwareness, fragmentName, user?.sub, user?.name],
  );

  const editor = useEditor(
    {
      editable: !readOnly,
      immediatelyRender: false,
      editorProps: {
        attributes: { class: 'nula-editor-root' },
        // Paste/drop of image files → compress + embed. Returns false (defers to
        // the normal paste/drop pipeline, incl. MarkdownPaste) when the payload
        // carries no image files, so text/markdown paste is unaffected.
        handlePaste: (_view, event) => {
          const files = Array.from(event.clipboardData?.files ?? []).filter(isImageFile);
          if (files.length === 0) return false;
          if (!editorRef.current) return false;
          void insertImageFiles(editorRef.current, files);
          return true;
        },
        handleDrop: (_view, event) => {
          const dt = (event as DragEvent).dataTransfer;
          const files = Array.from(dt?.files ?? []).filter(isImageFile);
          if (files.length === 0) return false;
          if (!editorRef.current) return false;
          event.preventDefault();
          void insertImageFiles(editorRef.current, files);
          return true;
        },
      },
      extensions,
    },
    [doc, awareness, caretAwareness, readOnly],
  );

  const { items: railItems, activeId: railActiveId, _setAnchors, jumpTo } =
    useHeadingAnchors({ editor: editor ?? null, scrollElRef: scrollAreaRef });
  // Wire the stable anchor-jump wrapper (passed into LinkClickExtension above)
  // to the latest jumpTo, so clicking a #heading link scrolls to that heading.
  jumpToRef.current = jumpTo;

  // Keep the document's card preview markup in sync with its content (debounced).
  // Edit-gated: read-only viewers don't write previews.
  usePreviewMarkup(editor ?? null, { documentId: entityId, enabled: !readOnly });

  // Register the TiptapEditApplier for this editor's entity kind into the ai-composer
  // registry. Only document is registered; other kinds (chat, canvas, space)
  // are not wired to replace-text in M2.
  useRegisterEditApplier(editor ?? null, editEntityKind);

  // AI orchestration — only active when we have a supported entity kind + edit access.
  // When editEntityKind is null (e.g. canvas/space rooms), the hook is still called
  // (hooks cannot be conditional) but with a null editor — the hook no-ops gracefully.
  const { run: runEditAction, accept: acceptEdit, discard: discardEdit, retry: retryEdit, status: aiStatus, pending: rawPending } =
    useEditAction(
      editEntityKind ? (editor ?? null) : null,
      entityId,
      editEntityKind ?? 'document',
    );
  const aiPending = rawPending as TiptapPending | undefined;

  // Compute the toolbar's position (relative to .scrollContent) from the pending
  // edit's doc position. Recomputes on scroll so it stays pinned under the block.
  // Anchor rect for the SuggestionReviewTooltip (text-mode preview/review).
  const [suggestionAnchorRect, setSuggestionAnchorRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (aiStatus !== 'review' || !editor || !aiPending || aiPending.kind !== 'text') {
      setSuggestionAnchorRect(null);
      return;
    }
    const container = scrollAreaRef.current;
    if (!container) return;

    const anchorPos = aiPending.to;

    const computePos = () => {
      try {
        const coords = editor.view.coordsAtPos(anchorPos);
        setSuggestionAnchorRect(
          new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top),
        );
      } catch {
        // coordsAtPos can throw when the editor is mid-update; silently skip
      }
    };

    computePos();
    container.addEventListener('scroll', computePos);
    return () => { container.removeEventListener('scroll', computePos); };
  }, [aiStatus, editor, aiPending]);

  useReviewKeyboardShortcuts(interactive ? aiStatus : 'idle', acceptEdit, discardEdit);

  const generateChartForRange = useCallback(
    (range: { from: number; to: number }) => {
      if (!editEntityKind || !editor) return;
      void runEditAction(
        buildChartEditRequest(entityId, editEntityKind, range),
        rangeToHtml(editor, range.from, range.to),
        undefined,
      );
    },
    [editEntityKind, entityId, runEditAction, editor],
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed || !editEntityKind) return;
    const api = getAiEditing(editor);
    // 'requesting' (pre-stream) paints the thinking caret at the target. During 'streaming'
    // the applier owns the caret (it paints at the live write position each chunk so it
    // FOLLOWS the text) — don't fight it here. Clear on any terminal/idle state.
    if (aiStatus === 'requesting' && aiPending?.kind === 'text') {
      api.setCaret({ pos: aiPending.to, state: 'thinking', label: 'Nula' });
    } else if (aiStatus !== 'streaming') {
      api.clearCaret();
    }
  }, [aiStatus, aiPending, editor, editEntityKind]);

  const [helpModalOpen, setHelpModalOpen] = useState(false);

  // Detect /? typed at the start of a line (or after whitespace) → open command reference modal.
  // Uses Tiptap's onUpdate transaction listener instead of a ProseMirror plugin so it can
  // reach React state (setHelpModalOpen) without a portal/event bridge.
  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => {
      const { state } = editor;
      const { selection } = state;
      if (!selection.empty) return;
      const { $from } = selection;
      const textBefore = $from.nodeBefore?.textContent ?? '';
      if (!textBefore.endsWith('/?')) return;
      // Only trigger at line start or after whitespace
      const beforeTrigger = textBefore.slice(0, -2);
      if (beforeTrigger.length > 0 && !/\s$/.test(beforeTrigger)) return;
      // Delete the /? characters then open modal
      editor.chain().deleteRange({ from: $from.pos - 2, to: $from.pos }).run();
      setHelpModalOpen(true);
    };
    editor.on('update', handleUpdate);
    return () => { editor.off('update', handleUpdate); };
  }, [editor]);

  // Detect `[[` (wiki-link trigger) → open the document picker to insert a
  // nula-link. Mirrors the /? detection: delete the trigger chars, then open.
  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => {
      const { state } = editor;
      const { selection } = state;
      if (!selection.empty) return;
      const { $from } = selection;
      const textBefore = $from.nodeBefore?.textContent ?? '';
      if (!textBefore.endsWith('[[')) return;
      // Delete the `[[` characters, then open the picker.
      editor.chain().deleteRange({ from: $from.pos - 2, to: $from.pos }).run();
      window.dispatchEvent(new Event('nula-show-doc-picker'));
    };
    editor.on('update', handleUpdate);
    return () => { editor.off('update', handleUpdate); };
  }, [editor]);

  const [linkEditorState, setLinkEditorState] = useState<{
    isOpen: boolean;
    mode: 'insert' | 'view';
    position: { top: number; left: number };
  }>({ isOpen: false, mode: 'insert', position: { top: 0, left: 0 } });

  const openLinkEditor = (mode: 'insert' | 'view' = 'insert') => {
    // Get position from editor's selection
    const view = editor?.view;
    if (!view) return;
    const { from } = view.state.selection;
    const coords = view.coordsAtPos(from);
    setLinkEditorState({
      isOpen: true,
      mode,
      position: { top: coords.bottom + 8, left: coords.left },
    });
  };

  // Keep editorRef current so editorProps paste/drop handlers reach the live
  // editor (they were bound at useEditor init, before `editor` existed).
  useEffect(() => {
    editorRef.current = editor ?? null;
  }, [editor]);

  // Imperatively enforce editable state. The `editable: !readOnly` config passed
  // at useEditor-init is NOT a reliable guarantee in Tiptap v3 + Collaboration —
  // a read-only-permission user could still type locally (changes that never
  // persist, which silently vanish). `setEditable` forces the live ProseMirror
  // view's editable state, so read-only genuinely blocks input.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    // `setEditable` is always present on a real Tiptap editor; guard so a
    // transitional/partial instance can never crash the component.
    if (typeof editor.setEditable !== 'function') return;
    if (lastSuspended.current !== suspended) {
      lastSuspended.current = suspended;
      editor.setEditable(interactive, false);
      return;
    }
    editor.setEditable(interactive);
  }, [editor, interactive, suspended]);

  // Wire the Link slash command: when the user selects "Link" from the slash
  // menu, it dispatches `nula-editor-open-link` on window so the menu stays
  // decoupled from this component's state.
  useEffect(() => {
    const handleOpenLink = () => openLinkEditor('insert');
    window.addEventListener('nula-editor-open-link', handleOpenLink);
    return () => window.removeEventListener('nula-editor-open-link', handleOpenLink);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Wire the Image slash command: it dispatches `nula-show-image-picker`, which
  // opens the hidden file input below.
  useEffect(() => {
    const openPicker = () => imageInputRef.current?.click();
    window.addEventListener('nula-show-image-picker', openPicker);
    return () => window.removeEventListener('nula-show-image-picker', openPicker);
  }, []);

  // Sidebar → editor: clicking a thread in CommentSidebar fires
  // `nula-editor-focus-comment` with { threadId }. Find the first mark position
  // in the doc, scroll the scroll area to it, then open the inline popover.
  useEffect(() => {
    if (!editor) return;
    const handler = (e: Event) => {
      const { threadId } = (e as CustomEvent<{ threadId: string }>).detail;
      const { state, view } = editor;
      const markType = state.schema.marks.comment;
      if (!markType) return;
      let foundPos: number | null = null;
      state.doc.nodesBetween(0, state.doc.content.size, (node, pos) => {
        if (foundPos !== null) return false;
        if (node.marks.some((m) => m.type === markType && m.attrs.threadId === threadId)) {
          foundPos = pos;
        }
      });
      if (foundPos === null) return;
      // Scroll the marked text into view and open the comment popover.
      view.dispatch(state.tr.scrollIntoView());
      editor.commands.setTextSelection(foundPos);
      const coords = view.coordsAtPos(foundPos);
      window.dispatchEvent(
        new CustomEvent('nula-show-comment', {
          detail: { threadId, top: coords.bottom + 6, left: coords.left },
        }),
      );
    };
    window.addEventListener('nula-editor-focus-comment', handler);
    return () => window.removeEventListener('nula-editor-focus-comment', handler);
  }, [editor]);

  // Heading-anchor extraction for the ScrollRail.
  // Heading-anchor extraction for the ScrollRail.
  // Walk the ProseMirror document tree to collect heading nodes with their
  // model positions — avoids all DOM geometry fragility for jump + tracking.
  useEffect(() => {
    if (!editor) return;

    const extractHeadings = () => {
      if (!editor.state?.doc) return;
      const scrollEl = scrollAreaRef.current;
      if (!scrollEl) return;

      // Build id→pos map from the ProseMirror doc (authoritative positions)
      const seen = new Map<string, number>();
      const posMap = new Map<string, number>();

      editor.state.doc.descendants((node, pos) => {
        if (node.type.name !== 'heading') return;
        const text = node.textContent.trim();
        if (!text) return;
        const base = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '').slice(0, 60) || 'heading';
        const count = (seen.get(base) ?? 0) + 1;
        seen.set(base, count);
        const id = count === 1 ? base : `${base}-${count}`;
        posMap.set(id, pos);
      });

      // Build anchors from DOM order (matches visual order) using posMap for positions.
      // Do NOT set el.id — Tiptap's ProseMirror reconciler wipes manually-set
      // attributes on every transaction. Jump uses text-match instead.
      const seen2 = new Map<string, number>();
      const anchors: Array<{ id: string; label: string; pos: number }> = [];
      const headingEls = Array.from(scrollEl.querySelectorAll<HTMLElement>('h1, h2, h3'));

      headingEls.forEach((el) => {
        const text = el.textContent?.trim() ?? '';
        if (!text) return;
        const base = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '').slice(0, 60) || 'heading';
        const count = (seen2.get(base) ?? 0) + 1;
        seen2.set(base, count);
        const id = count === 1 ? base : `${base}-${count}`;
        const pos = posMap.get(id);
        if (pos !== undefined) anchors.push({ id, label: text, pos });
      });

      _setAnchors(anchors);
    };

    editor.on('update', extractHeadings);
    extractHeadings();

    return () => {
      editor.off('update', extractHeadings);
    };
  }, [editor, _setAnchors, synced]);

  const contentMounted = synced && !!doc && !!awareness;

  if (!contentMounted) {
    return (
      <div data-testid="editor-loading">
        <EditorSkeleton />
      </div>
    );
  }

  return (
    <Flex vertical className={styles.root}>
      <EntityTitleHeader entityId={entityId} showTitle={showTitle} />
      {showActionbar && interactive && (
        <Actionbar documentId={entityId} presenceViewId={presenceViewId} />
      )}
      {/* Renders even when readOnly: a viewer who cannot edit still sees every
          surface and can switch between them (ui-design.md §9.6) — the orb is
          the only entry point to the surface panel, so gating it on readOnly
          made every non-body surface unreachable, not just presence-invisible.
          canEdit is passed separately so the panel can still suppress
          create/rename/move/delete for that viewer. */}
      {showActionbar && SurfaceOrb && (
        <SurfaceOrb
          entityId={entityId}
          onActiveViewChange={setPresenceViewId}
          canEdit={canEditSurfaces}
        />
      )}
      {/* Floating menus — rendered outside the scroll container so they can
          position relative to the viewport, not the scrolling area */}
      {interactive && editor && (
        <EditorBubbleMenu
          editor={editor}
          onEditLink={() => openLinkEditor('insert')}
          entityId={editEntityKind ? entityId : undefined}
          entityKind={editEntityKind ?? undefined}
          aiStatus={editEntityKind ? aiStatus : undefined}
          onAiPick={editEntityKind ? (pick) => {
            const req = pick.action === 'generate-chart'
              ? buildChartEditRequest(entityId, editEntityKind, { from: pick.from, to: pick.to })
              : { action: pick.action, entityKind: editEntityKind, entityId, target: { kind: 'tiptap-range' as const, from: pick.from, to: pick.to }, params: pick.params };
            void runEditAction(
              req,
              pick.blockContent,
              pick.action === 'generate-chart' ? undefined : 'review',
            );
          } : undefined}
          selectionTemplates={canGenerateFromSelection ? generateFromSelectionTemplates : undefined}
          onGenerateFromSelection={canGenerateFromSelection ? (templateId) => {
            requestGenerateFromSelection(editor, {
              entityId,
              fragmentName: fragmentName ?? 'prosemirror',
              templateId,
            });
          } : undefined}
        />
      )}
      {interactive && editor && <EditorTableMenu editor={editor} />}
      {interactive && editor && <EditorBlockHandle editor={editor} />}
      {interactive && editor && editEntityKind && (
        <EditorChartGutter editor={editor} onGenerateChart={generateChartForRange} />
      )}
      {interactive && editor && <EditorDocPicker editor={editor} currentEntityId={entityId} />}
      {interactive && editor && <EditorCanvasPicker editor={editor} />}
      {interactive && editor && <EditorEmojiPicker editor={editor} />}
      {interactive && editor && <EditorFind editor={editor} />}
      {editor && <EditorEntityLinkPreview editor={editor} />}
      {interactive && editor && doc && <EditorComments editor={editor} doc={doc} />}
      {/* Hidden picker for the Image slash command (raw <input type="file"> is an
          allowed exception). Compresses + embeds the chosen image(s). */}
      {interactive && (
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0 && editor) {
              void insertImageFiles(editor, Array.from(files));
            }
            e.target.value = ''; // allow re-picking the same file
          }}
        />
      )}
      <CommandReferenceModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
      {interactive && editor && linkEditorState.isOpen && (
        <EditorLinkEditor
          editor={editor}
          isOpen={linkEditorState.isOpen}
          mode={linkEditorState.mode}
          position={linkEditorState.position}
          onClose={() => setLinkEditorState((s) => ({ ...s, isOpen: false }))}
          onSwitchToEdit={() => setLinkEditorState((s) => ({ ...s, mode: 'insert' }))}
        />
      )}
      {/* scrollWrapper — positioning context for the rail */}
      <Flex vertical className={styles.scrollWrapper}>
        {railItems.length > 0 && (
          <ScrollRail
            className={styles.scrollRail}
            items={railItems}
            activeId={railActiveId}
            onJump={jumpTo}
            onScrollToTop={() => scrollAreaRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        )}
        {/* scrollArea — the actual scroll container */}
        <Flex
          vertical
          ref={scrollAreaRef}
          className={styles.scrollArea}
          data-scroll-container="editor"
        >
          <Flex
            vertical
            className={styles.scrollContent}
          >
            <Flex vertical className={styles.contentBlock}>
              <EditorContent
                editor={editor}
                data-testid="editor"
              />
              {!readOnly && editEntityKind && aiStatus === 'error' && (
                <Flex align="center" gap="sm" data-testid="ai-error-banner">
                  <Text>Something went wrong with the AI action.</Text>
                  <Button size="sm" variant="text" htmlType="button" onClick={() => retryEdit()}>
                    Try again
                  </Button>
                </Flex>
              )}
            </Flex>
            {!readOnly && editEntityKind && aiStatus === 'review' && aiPending?.kind === 'text' && (
              <SuggestionReviewTooltip
                open
                anchorRect={suggestionAnchorRect}
                onAccept={acceptEdit}
                onDiscard={discardEdit}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
